import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import firebase from "firebase";

import { projectFirestore } from "../../../../firebase-config/config";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import "./PlantationOneDashboard.scss";

const PlantationOneDashboard = () => {
  const { user } = useAuthContext();
  const [adoptedTrees, setAdoptedTrees] = useState([]);
  const [plantCount, setPlantCount] = useState(null); // ✅ null initially
  const navigate = useNavigate();

  // 🔒 Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // 🌱 Fetch trees
  useEffect(() => {
    const fetchTrees = async () => {
      if (!user) return;
      let response;

      if (user?.role === "admin") {
        response = projectFirestore
          .collection("orders")
          .orderBy("createdAt", "desc");
      } else {
        response = projectFirestore
          .collection("orders")
          .where("userId", "==", user.phoneNumber)
          .orderBy("createdAt", "desc");
      }

      const treeDocs = await response.get();
      if (treeDocs.empty) {
        setPlantCount(0);
        return;
      }

      const orders = treeDocs.docs.map((doc) => {
        const item = doc.data();
        const lastTimeline = item.timeline?.[item.timeline.length - 1] || {};
        const userName =
          lastTimeline.createdBy?.userName ||
          item.userName ||
          "Unknown User";

        return { ...item, id: doc.id, userName };
      });

      const totalProducts = orders.reduce(
        (count, order) => count + (order.products?.length || 0),
        0
      );

      setPlantCount(totalProducts);
      setAdoptedTrees(orders);
    };

    fetchTrees();
  }, [user]);

  // 🔧 Utility to strip HTML tags
  const stripHTML = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <div className="p-3 p-sm-4 plantation-dashboard mb-5">
      <div className="plantation-dashboard-header">
        <h1>
          {user?.role === "admin"
            ? "Welcome, Admin"
            : `Welcome, ${user?.fullName || "Planter"}`}
        </h1>
        <p>
          {user?.role === "admin"
            ? "Here’s a look at all adopted trees by users."
            : "Here's a look at how your adopted trees are growing over time."}
        </p>
      </div>

      <div className="plantation-dashboard-content">
        <h4>
  {plantCount === null ? "" : user?.role === "admin" ? (
    `🎉 Great! ${plantCount} plants have been adopted so far!`
  ) : plantCount > 0 ? (
    "🌿 Great! Thank you for your contribution to the environment!"
  ) : (
    ""
  )}
</h4>

        {plantCount === 0 && user?.role !== "admin" && (
  <div className="no-adoption-message text-center mt-4">
    <h5 className="fw-bold mb-2">🌱 Start your green journey today!</h5>
    <p className="mb-3">
      You haven’t adopted any trees yet. Take the first step towards a cleaner & greener tomorrow. 🌍
    </p>
    <button
      className="btn btn-success"
      onClick={() => navigate("/products-two")}
    >
      Adopt Tree Now
    </button>
  </div>
)}




        {plantCount > 0 && (
          <div className="plantation-dashboard-grid">
            {adoptedTrees?.map((item, index) => {
              const data = {
                products: item.products,
                id: item.id,
                createdAt: item.createdAt,
                userName: item.createdBy?.userName || item.userName || "Unknown User",
              };

              return (
                <PlantationOneDashboardCard
                  key={index}
                  data={data}
                  isAdmin={user?.role === "admin"}
                  stripHTML={stripHTML}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ✅ Card Component (same file me)
function PlantationOneDashboardCard({ data, isAdmin, stripHTML }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  const productIds = useMemo(
    () => data.products.map((product) => product.id),
    [data.products]
  );

  useEffect(() => {
    const getProducts = async () => {
      if (!productIds.length) return;
      const response = projectFirestore
        .collection("products")
        .where(firebase.firestore.FieldPath.documentId(), "in", productIds);

      const productDocs = await response.get();
      if (productDocs.empty) return;

      const products = productDocs.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setProducts(products);
    };
    getProducts();
  }, [productIds]);

  return (
    <>
      {products.map((product) => (
        <div
          className="plantation-dashboard-card"
          key={`${data.id}-${product.id}`}
        >
          <div className="plantation-card-image">
            <img
              src={
                typeof product.images?.[0] === "string"
                  ? product.images[0]
                  : product.images?.[0]?.url
              }
              alt="Plant"
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/no-image-available.png"; // fallback
              }}
            />
          </div>

          <div className="p-3 plantation-card-content">
            <div className="plantation-card-text">
              <div className="plantation-card-title">
                <div className="d-flex flex-column gap-1">
                  <h3>{product.name}</h3>
                  {isAdmin && (
                    <p className="plantation-card-user">
                      Adopted by: <strong>{data.userName}</strong>
                    </p>
                  )}
                </div>
                <p>
                  Adopted on:{" "}
                  <span>{format(data.createdAt.toDate(), "MMM dd, yyyy")}</span>
                </p>
              </div>

              <p className="plantation-card-description mb-2">
                {product.short_description
                  ? stripHTML(product.short_description).length > 120
                    ? stripHTML(product.short_description).slice(0, 120) + "..."
                    : stripHTML(product.short_description)
                  : ""}
              </p>
            </div>

            <div className="plantation-card-actions">
              <button
                onClick={() => {
                  navigate(
                    `/my-plants/${data.id}/${product.name
                      .split(" ")
                      .join("-")
                      .toLowerCase()}/${product.id}`
                  );
                }}
                style={{ width: "100%" }}
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

export default PlantationOneDashboard;
